<div class="main-view space-y-2 md:space-y-4">
  <app-page-surface-header titleIcon="billing"
    title="Payments"
    subtitle="Money received and money waiting to be verified."
  />

  @if (actionMsg) {
  <p class="rounded-lg bg-success/10 px-3 py-2 text-sm text-success">{{ actionMsg }}</p>
  }
  @if (actionErr) {
  <p class="rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">{{ actionErr }}</p>
  }

  @if (pending.length) {
  <app-page-body-panel>
    <div pageToolbar class="flex flex-wrap items-center gap-2">
      <h2 class="text-sm font-bold text-black dark:text-white">
        Pending verification
        <span class="ml-1 rounded-full bg-warning/20 px-2 py-0.5 text-xs font-medium text-warning">{{ pending.length }}</span>
      </h2>
    </div>
    <app-list-module-table
      variant="surface"
      [columns]="pendingColumns"
      [rows]="pending"
      rowTrackKey="id"
      emptyMessage="Nothing awaiting verification."
    >
      <ng-template listCellDef="client" let-row="row"><span class="text-black dark:text-white">{{ row.tenant?.tenant_name || ('Client #' + row.tenant_id) }}</span></ng-template>
      <ng-template listCellDef="invoice" let-row="row"><span class="font-medium text-black dark:text-white">{{ row.receipt }}</span></ng-template>
      <ng-template listCellDef="amount" let-row="row"><span class="whitespace-nowrap">{{ row.currency }} {{ row.amount | number:'1.2-2' }}</span></ng-template>
      <ng-template listCellDef="method" let-row="row"><span class="uppercase">{{ row.offline_method || '—' }}</span></ng-template>
      <ng-template listCellDef="reference" let-row="row">{{ row.reference || '—' }}</ng-template>
      <ng-template listCellDef="paid_on" let-row="row">{{ row.paid_date ? (row.paid_date | date:'mediumDate') : '—' }}</ng-template>
      <ng-template listCellDef="actions" let-row="row">
        <div class="flex justify-end gap-2">
          <button type="button" class="rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/5" (click)="viewReceipt(row.id)">View receipt</button>
          @if (P.BILLING_MANAGE | hasPermission) {
          <button type="button" class="rounded-lg bg-success px-2.5 py-1 text-xs font-medium text-white hover:bg-success/90 disabled:opacity-50" [disabled]="paymentActioning" (click)="confirmPayment(row.id)">Confirm</button>
          <button type="button" class="rounded-lg bg-danger px-2.5 py-1 text-xs font-medium text-white hover:bg-danger/90 disabled:opacity-50" [disabled]="paymentActioning" (click)="rejectPayment(row.id)">Reject</button>
          }
        </div>
      </ng-template>
    </app-list-module-table>
  </app-page-body-panel>
  }

  <app-list-module-surface
    [config]="listSurfaceConfig"
    [filters]="listFilters"
    [loading]="loading"
    [errorMessage]="error"
    [meta]="meta"
    [rowCount]="payments.length"
    (filtersChange)="onListFiltersChange($event)"
    (pageChange)="onListPageChange($event)"
  >
    <ng-container toolbarFiltersExtra>
      <div class="relative">
        <select
          class="form-select bg-white py-3 px-4 text-white-dark rounded-full border-white-light dark:border-[#191e3a] dark:bg-dark"
          [(ngModel)]="tenantFilter"
          (ngModelChange)="onFilterChange()"
        >
          <option [ngValue]="null">All clients</option>
          <option *ngFor="let c of clients" [ngValue]="c.id">{{ clientLabel(c) }}</option>
        </select>
      </div>
      <div class="relative">
        <select
          class="form-select bg-white py-3 px-4 text-white-dark rounded-full border-white-light dark:border-[#191e3a] dark:bg-dark"
          [(ngModel)]="methodFilter"
          (ngModelChange)="onFilterChange()"
        >
          <option value="">All methods</option>
          <option value="razorpay">Razorpay (online)</option>
          <option value="offline">Offline</option>
        </select>
      </div>
    </ng-container>

    <app-list-module-table
      listTable
      variant="surface"
      [columns]="columns"
      [rows]="payments"
      [loading]="loading"
      emptyMessage="No payments found."
      rowTrackKey="id"
    >
      <ng-template listCellDef="created_at" let-row="row">
        <span class="whitespace-nowrap text-xs text-gray-500">{{ row.created_at | date:'dd MMM y, HH:mm' }}</span>
      </ng-template>
      <ng-template listCellDef="client" let-row="row">
        <span class="font-medium text-black dark:text-white">{{ tenantLabel(row) }}</span>
      </ng-template>
      <ng-template listCellDef="invoice" let-row="row">
        <a class="font-mono text-xs text-primary hover:underline" routerLink="/invoices" [queryParams]="{ client: row.tenant_id }">{{ row.receipt }}</a>
      </ng-template>
      <ng-template listCellDef="purpose" let-row="row">
        <span class="text-xs text-gray-600 dark:text-gray-300">{{ purposeLabel(row) }}</span>
      </ng-template>
      <ng-template listCellDef="method" let-row="row">
        <span class="rounded px-1.5 py-0.5 text-xs" [ngClass]="row.method === 'offline' ? 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300' : 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300'">
          {{ row.method === 'offline' ? 'Offline' : 'Razorpay' }}
        </span>
      </ng-template>
      <ng-template listCellDef="status" let-row="row">
        <span class="rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="statusClass(row.status)">{{ row.status }}</span>
      </ng-template>
      <ng-template listCellDef="amount" let-row="row">
        <span class="whitespace-nowrap">{{ row.currency }} {{ row.amount | number:'1.2-2' }}</span>
      </ng-template>
      <ng-template listCellDef="actions" let-row="row">
        <div class="inline-flex items-center justify-end gap-1">
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-primary hover:bg-primary/10"
            title="Details"
            aria-label="Details"
            (click)="openDetails(row)"
          >
            <icon-eye class="h-4 w-4" />
          </button>
        </div>
      </ng-template>
    </app-list-module-table>
  </app-list-module-surface>

  <!-- Details modal -->
  <div *ngIf="selected" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" (click)="closeDetails()" (keydown.escape)="closeDetails()">
    <div class="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl dark:bg-[#0e1726]" (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
      <div class="mb-3 flex items-center justify-between">
        <h2 class="text-base font-bold text-black dark:text-white">Payment #{{ selected.id }}</h2>
        <button type="button" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" (click)="closeDetails()">✕</button>
      </div>
      <dl class="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
        <div class="flex justify-between sm:block"><dt class="text-gray-500">Status</dt><dd><span class="rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="statusClass(selected.status)">{{ selected.status }}</span></dd></div>
        <div class="flex justify-between sm:block"><dt class="text-gray-500">Amount</dt><dd class="font-medium">{{ selected.currency }} {{ selected.amount | number:'1.2-2' }}</dd></div>
        <div class="flex justify-between sm:block"><dt class="text-gray-500">Invoice total</dt><dd class="font-medium">{{ selected.currency }} {{ selected.invoice?.total | number:'1.2-2' }}</dd></div>
        <!-- Approving a partial receipt is only a sound decision with the balance in view. -->
        <div class="flex justify-between sm:block"><dt class="text-gray-500">Outstanding balance</dt><dd class="font-medium">{{ selected.currency }} {{ selected.invoice?.balance | number:'1.2-2' }}</dd></div>
        <div class="flex justify-between sm:block"><dt class="text-gray-500">Client</dt><dd>{{ tenantLabel(selected) }}</dd></div>
        <div class="flex justify-between sm:block"><dt class="text-gray-500">Invoice</dt><dd class="font-mono text-xs">{{ selected.receipt }}</dd></div>
        <div class="flex justify-between sm:block"><dt class="text-gray-500">For</dt><dd>{{ purposeLabel(selected) }}</dd></div>
        <div class="flex justify-between sm:block"><dt class="text-gray-500">Method</dt><dd>{{ selected.method === 'offline' ? 'Offline' : 'Razorpay' }}</dd></div>
        <div class="flex justify-between sm:block"><dt class="text-gray-500">Created</dt><dd>{{ selected.created_at | date:'dd MMM y, HH:mm' }}</dd></div>

        <ng-container *ngIf="selected.method !== 'offline'">
          <div class="flex justify-between sm:block"><dt class="text-gray-500">Order id</dt><dd class="break-all font-mono text-xs">{{ selected.provider_order_id || '—' }}</dd></div>
          <div class="flex justify-between sm:block"><dt class="text-gray-500">Payment id</dt><dd class="break-all font-mono text-xs">{{ selected.provider_payment_id || '—' }}</dd></div>
          <div class="flex justify-between sm:block"><dt class="text-gray-500">Signature</dt><dd>{{ selected.signature ? 'verified ✓' : '—' }}</dd></div>
        </ng-container>

        <ng-container *ngIf="selected.method === 'offline'">
          <div class="flex justify-between sm:block"><dt class="text-gray-500">Offline method</dt><dd class="uppercase">{{ selected.offline_method || '—' }}</dd></div>
          <div class="flex justify-between sm:block"><dt class="text-gray-500">Reference</dt><dd>{{ selected.reference || '—' }}</dd></div>
          <div class="flex justify-between sm:block"><dt class="text-gray-500">Paid on</dt><dd>{{ selected.paid_date ? (selected.paid_date | date:'mediumDate') : '—' }}</dd></div>
          <div class="flex justify-between sm:block"><dt class="text-gray-500">Verified at</dt><dd>{{ selected.verified_at ? (selected.verified_at | date:'dd MMM y, HH:mm') : '—' }}</dd></div>
        </ng-container>

        <div *ngIf="selected.error_reason" class="sm:col-span-2"><dt class="text-gray-500">Error</dt><dd class="text-danger">{{ selected.error_reason }}</dd></div>
        <div *ngIf="selected.rejection_reason" class="sm:col-span-2"><dt class="text-gray-500">Rejection reason</dt><dd class="text-danger">{{ selected.rejection_reason }}</dd></div>
      </dl>

      <div class="mt-5 flex justify-end gap-2">
        <button *ngIf="selected.method === 'offline' && selected.receipt_path" type="button" class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/5" (click)="viewReceipt(selected.id)">View receipt</button>
        <button type="button" class="rounded-lg bg-[#303030] px-4 py-1.5 text-xs font-semibold text-white hover:bg-black" (click)="closeDetails()">Close</button>
      </div>
    </div>
  </div>
</div>
